import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { tasksApi, Task } from '@/lib/api';
import { ClipboardList, Plus } from 'lucide-react';
import { Button, Card, EmptyState, Spinner, Tabs, toast } from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { TaskCard } from './components/TaskCard';
import { TaskSheet } from './components/TaskSheet';
import { useAufgabeErledigen } from './useAufgabeErledigen';

type FilterTab = 'all' | 'today' | 'overdue' | 'completed';

/** HTTP-Status eines Fehlers aus fetchApi (axios), sonst undefined. */
function httpStatus(fehler: unknown): number | undefined {
  return (fehler as { response?: { status?: number } } | null)?.response?.status;
}

export function TasksPage() {
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  // Über react-query, damit eine Aufgabe aus Schnell erfassen sofort erscheint.
  const { data: tasks = [], isLoading: loading } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => tasksApi.list(),
  });

  const handleStatusChange = useAufgabeErledigen();

  // /tasks/<id> öffnet die Aufgabe im Sheet über der Liste. Dorthin verweisen
  // die Wiedervorlage auf der Interaktionsseite und die Benachrichtigungen
  // task.due_today/task.overdue (action_url aus elbklar_core.jobs.task_deadlines).
  // Eine eigene Detailseite für Aufgaben gibt es nicht.
  //
  // Die Aufgabe kommt über GET /tasks/{id}, nicht aus der Liste: Die Liste im
  // Cache kann veraltet sein (invalidateQueries ohne Beobachter lädt nicht
  // nach, beim Öffnen stünde die alte Liste da und eine eben angelegte
  // Wiedervorlage fehlte). Geöffnet wird nach dem Laden; „nicht gefunden“
  // nur bei einem echten 404.
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const verwiesen = useQuery({
    queryKey: ['tasks', 'einzeln', taskId],
    queryFn: () => tasksApi.get(taskId!),
    enabled: !!taskId,
    retry: (versuche, fehler) => httpStatus(fehler) !== 404 && versuche < 1,
  });
  const geoeffnet = useRef<string | null>(null);
  useEffect(() => {
    if (!taskId || verwiesen.isFetching || geoeffnet.current === taskId) return;
    const task = verwiesen.data;
    if (task?.id === taskId) {
      geoeffnet.current = taskId;
      setActiveTab(task.status === 'completed' || task.status === 'cancelled' ? 'completed' : 'all');
      setEditingTask(task);
      setShowModal(true);
    } else if (verwiesen.error) {
      geoeffnet.current = taskId;
      toast.error(httpStatus(verwiesen.error) === 404 ? 'Aufgabe nicht gefunden' : 'Aufgabe ließ sich nicht laden');
      navigate('/tasks', { replace: true });
    }
  }, [taskId, verwiesen.isFetching, verwiesen.data, verwiesen.error, navigate]);

  const handleEdit = (task: Task) => {
    setEditingTask(task);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingTask(null);
    setShowModal(true);
  };

  const handleOpenChange = (offen: boolean) => {
    setShowModal(offen);
    if (!offen) {
      setEditingTask(null);
      // Zurück auf /tasks, ohne Eintrag im Verlauf: „Zurück“ führt dahin, woher man kam.
      if (taskId) {
        geoeffnet.current = null;
        navigate('/tasks', { replace: true });
      }
    }
  };

  // Filter tasks based on active tab
  const filteredTasks = tasks.filter((task) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = task.due_date ? new Date(task.due_date) : null;
    if (dueDate) dueDate.setHours(0, 0, 0, 0);

    switch (activeTab) {
      case 'today':
        return dueDate && dueDate.getTime() === today.getTime() &&
               task.status !== 'completed' && task.status !== 'cancelled';
      case 'overdue':
        return dueDate && dueDate < today &&
               task.status !== 'completed' && task.status !== 'cancelled';
      case 'completed':
        return task.status === 'completed' || task.status === 'cancelled';
      case 'all':
      default:
        return task.status !== 'completed' && task.status !== 'cancelled';
    }
  });

  // Count for each tab
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);

  const counts = {
    all: tasks.filter((t) => t.status !== 'completed' && t.status !== 'cancelled').length,
    today: tasks.filter((t) => {
      if (!t.due_date || t.status === 'completed' || t.status === 'cancelled') return false;
      const d = new Date(t.due_date);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === todayDate.getTime();
    }).length,
    overdue: tasks.filter((t) => {
      if (!t.due_date || t.status === 'completed' || t.status === 'cancelled') return false;
      const d = new Date(t.due_date);
      d.setHours(0, 0, 0, 0);
      return d < todayDate;
    }).length,
    completed: tasks.filter((t) => t.status === 'completed' || t.status === 'cancelled').length,
  };

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'Alle offen', count: counts.all },
    { key: 'today', label: 'Heute', count: counts.today },
    { key: 'overdue', label: 'Überfällig', count: counts.overdue },
    { key: 'completed', label: 'Erledigt', count: counts.completed },
  ];

  const neuKnopf = (
    <Button variant="primary" icon={Plus} onClick={handleCreate}>
      Aufgabe anlegen
    </Button>
  );

  if (loading) {
    return (
      <>
        <CrmPageHeader title="Aufgaben" />
        <div className="flex min-h-[400px] items-center justify-center">
          <Spinner size={32} />
        </div>
      </>
    );
  }

  return (
    <div>
      <CrmPageHeader title="Aufgaben" subtitle={`${counts.all} offen`} actions={neuKnopf}>
        <Tabs
          className="w-full"
          label="Aufgaben filtern"
          value={activeTab}
          onChange={(v) => setActiveTab(v as FilterTab)}
          items={tabs.map((tab) => ({ value: tab.key, label: tab.label, count: tab.count > 0 ? tab.count : undefined }))}
        />
      </CrmPageHeader>

      {/* Task List */}
      <div className="mt-4">
        {filteredTasks.length > 0 ? (
          <Card flush>
            {filteredTasks.map((task) => (
              <TaskCard key={task.id} task={task} onStatusChange={handleStatusChange} onEdit={handleEdit} />
            ))}
          </Card>
        ) : (
          <EmptyState
            icon={ClipboardList}
            title="Keine Aufgaben"
            description={
              activeTab === 'completed'
                ? 'Erledigte Aufgaben erscheinen hier.'
                : activeTab === 'overdue'
                  ? 'Keine überfälligen Aufgaben.'
                  : activeTab === 'today'
                    ? 'Keine Aufgaben für heute.'
                    : 'Offene Aufgaben erscheinen hier, sobald eine angelegt ist.'
            }
            action={activeTab !== 'completed' && activeTab !== 'overdue' ? neuKnopf : undefined}
          />
        )}
      </div>

      <TaskSheet open={showModal} onOpenChange={handleOpenChange} task={editingTask} />
    </div>
  );
}
